// Alerts — filter by severity + state; acknowledge and resolve via the API (server-side
// alert rows only — never a trade action). Shows each alert's threshold vs the account's
// live current value, ported from the mockup rvAlerts. API enums are mapped to the mockup's
// severity/state visuals.

import { useState } from 'react';
import * as api from '../api/client';
import type { Alert } from '../types/api';
import { C, MONO, SEV } from '../lib/tokens';
import { fmtAge, fmtMoney, fmtPctStr, fmtTS } from '../lib/format';
import { hexA } from '../lib/tokens';
import { badgeS, CHIP_BASE, CHIP_ON, GBTN_SM } from '../lib/styles';
import { isActiveAlert } from '../lib/selectors';
import { derive, type Derived } from '../lib/mappers';
import { EmptyState } from '../components/common';
import { useData, useServerNow, useUi } from '../store/hooks';
import { openDrawer, toastMsg, uiStore } from '../store/uiStore';
import { upsertAlertInStore } from '../store/dataStore';

const STATE_ORDER: Record<string, number> = { pending: 0, firing: 0, acknowledged: 1, resolved: 2 };
const SEV_ORDER: Record<string, number> = { critical: 0, warning: 1, info: 2 };

function currentValue(al: Alert, d: Derived | undefined, currency: string): string {
  if (!d) return '—';
  switch (al.metric) {
    case 'margin_level':
    case 'mlvl':
      return d.mlvlNum == null ? 'Unavailable' : Math.round(d.mlvlNum) + '%';
    case 'drawdown_pct':
    case 'dd':
      return fmtPctStr(d.ddNum == null ? null : String(d.ddNum));
    case 'daily_pct':
    case 'daily':
      return d.dailyPctNum == null ? 'Unavailable' : fmtPctStr(String(d.dailyPctNum), true);
    case 'heartbeat':
    case 'hb':
      return fmtAge(d.hbAgeMs) + ' silent';
    case 'balance':
    case 'bal':
      return fmtMoney(d.balNum == null ? null : String(d.balNum), currency);
    case 'broker_link':
    case 'bc':
      return d.bc;
    default:
      return '—';
  }
}

export function Alerts() {
  const alerts = useData((s) => s.alerts);
  const accountsById = useData((s) => s.accountsById);
  const thr = useUi((s) => s.thr);
  const aSev = useUi((s) => s.aSev);
  const aState = useUi((s) => s.aState);
  const serverNow = useServerNow();
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  function matchState(a: Alert): boolean {
    if (!aState) return true;
    if (aState === 'active') return isActiveAlert(a);
    return a.state === aState;
  }

  const list = alerts
    .filter((a) => (!aSev || a.severity === aSev) && matchState(a))
    .slice()
    .sort(
      (p, q) =>
        STATE_ORDER[p.state]! - STATE_ORDER[q.state]! ||
        SEV_ORDER[p.severity]! - SEV_ORDER[q.severity]! ||
        Date.parse(q.started_at) - Date.parse(p.started_at),
    );

  async function doAck(id: string) {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const res = await api.ackAlert(id);
      upsertAlertInStore(res.alert);
      toastMsg('Alert acknowledged.');
    } catch (err) {
      toastMsg('Could not acknowledge: ' + (err instanceof Error ? err.message : 'error'));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  }

  async function doResolve(id: string) {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const res = await api.resolveAlert(id);
      upsertAlertInStore(res.alert);
      toastMsg('Alert resolved.');
    } catch (err) {
      toastMsg('Could not resolve: ' + (err instanceof Error ? err.message : 'error'));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  }

  const sevChips: Array<[typeof aSev, string]> = [
    ['', 'All severities'],
    ['critical', '◆ Critical'],
    ['warning', '▲ Warning'],
    ['info', 'i Info'],
  ];
  const stateChips: Array<[typeof aState, string]> = [
    ['', 'All states'],
    ['active', 'Active'],
    ['acknowledged', 'Acknowledged'],
    ['resolved', 'Resolved'],
  ];

  return (
    <section data-screen-label="Alerts" style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Alerts</h1>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} role="group" aria-label="Severity filter">
          {sevChips.map(([v, label]) => (
            <button key={label} onClick={() => uiStore.set({ aSev: v })} aria-pressed={aSev === v} style={aSev === v ? CHIP_ON : CHIP_BASE}>
              {label}
            </button>
          ))}
        </div>
        <span style={{ width: 1, height: 18, background: C.ln }} aria-hidden="true" />
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} role="group" aria-label="State filter">
          {stateChips.map(([v, label]) => (
            <button key={label} onClick={() => uiStore.set({ aState: v })} aria-pressed={aState === v} style={aState === v ? CHIP_ON : CHIP_BASE}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map((al) => {
          const tok = SEV[al.severity]!;
          const d = derive0(accountsById[al.account_id], serverNow, thr);
          const account = accountsById[al.account_id];
          const active = isActiveAlert(al);
          const stText = active
            ? 'Active · ' + fmtAge(serverNow - Date.parse(al.started_at))
            : al.state === 'acknowledged'
              ? 'Acknowledged'
              : 'Resolved · lasted ' + fmtAge((al.resolved_at ? Date.parse(al.resolved_at) : serverNow) - Date.parse(al.started_at));
          const stColor = active ? tok[1] : al.state === 'acknowledged' ? C.bl : C.gy;
          return (
            <div
              key={al.id}
              style={{
                display: 'flex',
                gap: 10,
                padding: '12px 14px',
                background: C.s1,
                border: '1px solid ' + (active && al.severity === 'critical' ? hexA(C.rd, 0.4) : C.ln),
                borderRadius: 8,
                opacity: al.state === 'resolved' ? 0.62 : 1,
              }}
            >
              <span style={{ fontFamily: MONO, color: tok[1], fontSize: 14, width: 22, textAlign: 'center', flexShrink: 0, marginTop: 1 }} aria-hidden="true">
                {tok[0]}
              </span>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{tok[2] + ' — ' + al.title}</div>
                <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.45 }}>{al.reason}</div>
                <div style={{ fontSize: 10.5, color: C.b9, fontFamily: MONO }}>
                  {'threshold ' + al.threshold_text + ' · current ' + currentValue(al, d, account?.currency ?? 'USD')}
                </div>
                <button onClick={() => openDrawer(al.account_id)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: C.bl, fontSize: 11, padding: 0, fontFamily: MONO }}>
                  {(account?.label ?? al.account_label) + ' ' + (account?.login_masked ?? '')} →
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
                <span title={new Date(al.started_at).toString()} style={{ fontSize: 10.5, color: C.dm, fontFamily: MONO }}>
                  started {fmtTS(Date.parse(al.started_at))}
                </span>
                <span style={badgeS(stColor)}>{stText}</span>
                <div style={{ display: 'flex', gap: 5 }}>
                  {active && (
                    <button disabled={busy[al.id]} onClick={() => doAck(al.id)} style={{ ...GBTN_SM, opacity: busy[al.id] ? 0.6 : 1 }}>
                      Acknowledge
                    </button>
                  )}
                  {al.state !== 'resolved' && (
                    <button disabled={busy[al.id]} onClick={() => doResolve(al.id)} style={{ ...GBTN_SM, opacity: busy[al.id] ? 0.6 : 1 }}>
                      Resolve
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {list.length === 0 && <EmptyState title="No alerts match" sub="Change the severity or state filter to see alert history." />}
    </section>
  );
}

function derive0(
  account: import('../types/api').AccountRow | undefined,
  serverNow: number,
  thr: import('../lib/mappers').Thresholds,
): Derived | undefined {
  return account ? derive(account, serverNow, thr) : undefined;
}
